import CartItem from 'models/CartItem';
import compose from 'recompose/compose';
import Customer from 'models/Customer';
import IndeterminateCircularProgress from 'shared/components/IndeterminateCircularProgress';
import KioskLog from 'models/KioskLog';
import PropTypes from 'prop-types';
import React from 'react';
import toJS from 'shared/components/toJS';
import { actions as productActions } from 'redux/domain/Product';
import { actions as cartActions } from 'redux/domain/Cart';
import { actions as notificationActions, SnackbarSettings } from 'redux/domain/Notification';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getCancelTokenSource, cancelRequest } from 'api/Helpers';
import { getStoreLocation } from 'shared/utils/storeLocationHelpers';
import ProductFull from '../components/ProductFull';
import ProductFamilyFull from '../components/ProductFamilyFull';
import withRouter from '../../../shared/utils/WithRouter';

class ProductContainer extends React.Component {
	constructor() {
		super();
		this.cancelTokenSource = getCancelTokenSource();
		this.addItemToCart = this.addItemToCart.bind(this);
		this.getProductFamilyOnDisplayAsync = this.getProductFamilyOnDisplayAsync.bind(this);
		this.getProductFamilyQuantityAsync = this.getProductFamilyQuantityAsync.bind(this);
		this.updateProductInfo = this.updateProductInfo.bind(this);
	}

	componentDidMount() {
		this.getProductAsync(this.props.id, this.props.sku);
		const currentProduct = this.getProductDetail(this.props.id);
		const currentStore = getStoreLocation();
		if (currentProduct != null && !this.props.sku.startsWith('PF*')) {
			if (currentProduct.store !== currentStore) {
				this.updateProductInfo();
			} else {
				this.props.getProductQuantity(this.cancelTokenSource.token, this.props.id, currentStore);
			}
		}
	}

	componentWillReceiveProps(nextProps) {
		const { clearUpdateMessages, disableNotification, showNotification } = this.props;
		// We have message(s) to display from a cart update.
		if (nextProps.cartUpdateMessages.length > 0) {
			const settings = new SnackbarSettings(true, nextProps.cartUpdateMessages);
			// hide after 5 seconds
			settings.autoHideDuration = 5000;
			settings.onClose = disableNotification;
			showNotification(settings, [clearUpdateMessages]);
		}
		if (nextProps.id !== this.props.id) {
			this.getProductAsync(nextProps.id, nextProps.sku);
		}
	}

	componentWillUpdate(nextProps) {
		if (nextProps.fetchError) {
			const log = new KioskLog(
				new Error('Fetch Error in ProductContainer'),
				`ProductSKU: ${nextProps.sku} - ProductId: ${nextProps.id} - Referrer URL: "${
					this.props.router.location.pathname
				}"`,
			);
			log.postMessage();
			this.props.router.navigate('/errorPage', { 
				state: { 
					text: `We couldn't load the product "${nextProps.sku}"`,
					nextLine: `(Product Id: ${nextProps.id})`,
				} 
			});
		}
	}

	componentWillUnmount() {
		// if a request is in progress stop it.
		cancelRequest(this.cancelTokenSource);
	}

	/**
	 * Retrieve product information from eSTORIS.
	 * This is necessary because we currently only have basic product info
	 * retrieved from subcategories. If the user clicks a product to view
	 * we need to fetch more information.
	 * @param {number} productId key to the product
	 */
	getProductAsync(productId, productSku) {
		const { getProduct, getProductFamily, updateFetchError } = this.props;
		try {
			if (productSku.startsWith('PF*')) {
				getProductFamily(this.cancelTokenSource.token, productSku, productId);
			} else {
				getProduct(this.cancelTokenSource.token, productId);
			}
		} catch (error) {
			const log = new KioskLog(error, `productId=${productId} - productSku=${productSku}`);
			log.postMessage();
			updateFetchError(error);
		}
	}

	/**
	 * Return product detail for a specified product key from the store if available.
	 * @param {number} key to the product.
	 * @returns Product detail object. May be an empty object.
	 */
	getProductDetail(productId) {
		const { products } = this.props;
		const productDetail = products[productId];
		const product = productDetail !== undefined ? productDetail.product : null;
		return product;
	}

	getProductFamilyOnDisplayAsync(productId) {
		this.props.getProductFamilyOnDisplay(this.cancelTokenSource.token, this.props.id, productId);
	}

	getProductFamilyQuantityAsync(productId) {
		this.props.getProductFamilyQuantity(this.cancelTokenSource.token, this.props.id, productId);
	}

	async updateProductInfo() {
		await this.props.getProductOnDisplay(
			this.cancelTokenSource.token,
			this.props.id,
			getStoreLocation(),
		);
		await this.props.getProductQuantity(
			this.cancelTokenSource.token,
			this.props.id,
			getStoreLocation(),
		);
	}

	/**
	 * Add a single item to the user's shopping cart or wish list.
	 * @param {Product} product
	 * @param {number} cartType 1: shopping cart, 2: wish list
	 * @param {number} quantity default is 1
	 */
	addItemToCart(product, cartType, quantity = 1) {
		const { addCartItem, customer } = this.props;
		try {
			this.cancelTokenSource = getCancelTokenSource();
			const cartItem = new CartItem(null);
			const selectedStoreId = getStoreLocation();
			cartItem.quantity = quantity;
			if (product.sku.startsWith('PF*')) {
				// this is a product family so there is only one product that can be selected.
				// also, product family members can have multiple product attribute values so the
				// productAttributeValue is set up as an array.
				const selectedProductFamilyMember = product.selectedComponents[0];
				if (selectedProductFamilyMember.isSoftkit) {
					// get selected attributes for soft kit in product family
					cartItem.productAttributeValues = selectedProductFamilyMember.productAttributeValue;
					if (selectedProductFamilyMember.selectedComponents === undefined) {
						// this condition occurs on page load and if no substitutions have been made
						// in this case we will need to create the ProductFamilyMemberAttributeValues
						selectedProductFamilyMember.addSelectedComponent = product.addSelectedComponent;
						selectedProductFamilyMember.removeSelectedComponent = product.removeSelectedComponent;
						selectedProductFamilyMember.getKitItem = product.getKitItem;
						product.buildOutSoftKitComponents(selectedProductFamilyMember);
					}

					// if the user has made subtitutions, the selected components will be populated for the kit product family member
					cartItem.productFamilyMemberAttributeValues = selectedProductFamilyMember.selectedComponents.map(
						component => component.id,
					);
				} else {
					// this is a non-kit product family member
					cartItem.productAttributeValues = selectedProductFamilyMember.productAttributeValue;
				}

				cartItem.sku = selectedProductFamilyMember.sku;
				cartItem.selectedProductFamilyMemberProductId = selectedProductFamilyMember.id;
			} else {
				// Component.id is the productAttributeValue
				cartItem.productAttributeValues = product.selectedComponents.map(component => component.id);
			}

			addCartItem(
				this.cancelTokenSource.token,
				customer.id,
				product.productId,
				selectedStoreId,
				cartType,
				cartItem,
			);
		} catch (e) {
			const log = new KioskLog(e, `product=${product.sku} - customer=${customer.id}`);
			log.postMessage();
		}
	}

	render() {
		const { id, isFetching, sku, categoryId } = this.props;
		const product = this.getProductDetail(id);

		// if we don't have an actual product loaded yet, return IndeterminateCircularProgress
		return product !== null ? (
			<div>
				{sku.startsWith('PF*') ? (
					<ProductFamilyFull
						product={product}
						addItemToCart={this.addItemToCart}
						getProductFamilyOnDisplay={this.getProductFamilyOnDisplayAsync}
						getProductFamilyQuantity={this.getProductFamilyQuantityAsync}
						categoryId={categoryId}
					/>
				) : (
					<ProductFull
						product={product}
						addItemToCart={this.addItemToCart}
						categoryId={categoryId}
					/>
				)}
			</div>
		) : (
			<IndeterminateCircularProgress size={50} isVisible={isFetching} />
		);
	}
}

ProductContainer.propTypes = {
	addCartItem: PropTypes.func.isRequired,
	cartUpdateMessages: PropTypes.instanceOf(Array).isRequired,
	clearUpdateMessages: PropTypes.func.isRequired,
	customer: PropTypes.instanceOf(Customer).isRequired,
	disableNotification: PropTypes.func.isRequired,
	getProduct: PropTypes.func.isRequired,
	getProductFamily: PropTypes.func.isRequired,
	getProductFamilyOnDisplay: PropTypes.func.isRequired,
	getProductFamilyQuantity: PropTypes.func.isRequired,
	getProductOnDisplay: PropTypes.func.isRequired,
	getProductQuantity: PropTypes.func.isRequired,
	id: PropTypes.number.isRequired,
	isFetching: PropTypes.bool.isRequired,
	products: PropTypes.instanceOf(Object).isRequired,
	showNotification: PropTypes.func.isRequired,
	sku: PropTypes.string.isRequired,
	updateFetchError: PropTypes.func.isRequired,
	categoryId: PropTypes.string,
};

const mapStateToProps = state => {
	// Immutable List
	const cartUpdateMessages = state.cart.get('cartUpdateMessages');
	const customer = state.user.get('customer');
	const fetchError = state.product.get('fetchError');
	const isFetching = state.product.get('isFetching');
	// Immutable Map
	const products = state.product.get('products');
	return {
		cartUpdateMessages,
		customer,
		fetchError,
		isFetching,
		products,
	};
};

ProductContainer.defaultProps = {
	categoryId: '',
};

const mapDispatchToProps = dispatch =>
	bindActionCreators({ ...productActions, ...cartActions, ...notificationActions }, dispatch);

// for testing purposes
export { ProductContainer as ProductContainerClass };

export default withRouter(compose(connect(mapStateToProps, mapDispatchToProps))(toJS(ProductContainer)));
